import * as React from 'react';
import WaveSurfer from 'wavesurfer.js';
import type { WaveSurferOptions } from 'wavesurfer.js';

interface UseWaveformProps extends Omit<WaveSurferOptions, 'container' | 'url'> {
  url?: string | Blob;
  onFinish?: (() => void) | undefined;
  autoplay?: boolean;
}

interface UseWaveformReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isReady: boolean;
  togglePlayPause: () => void;
  seekTo: (progress: number) => void;
}

/**
 * Custom hook to manage Wavesurfer.js instance.
 * Handles initialization, events, and cleanup.
 */
export const useWaveform = ({
  url,
  onFinish,
  autoplay,
  ...options
}: UseWaveformProps): UseWaveformReturn => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const wavesurfer = React.useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [isReady, setIsReady] = React.useState(false);

  // Initialize Wavesurfer
  React.useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: 40,
      waveColor: '#C4C4C4',
      progressColor: '#555555',
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 3,
      barRadius: 2,
      normalize: true,
      ...options,
    });

    wavesurfer.current = ws;

    // Event Listeners
    ws.on('ready', () => {
      setIsReady(true);
      setDuration(ws.getDuration());
      if (autoplay) {
        ws.play();
      }
    });

    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));

    ws.on('timeupdate', (time: number) => {
      setCurrentTime(time);
    });

    ws.on('finish', () => {
      setIsPlaying(false);
      onFinish?.();
    });

    return () => {
      try {
        ws.destroy();
      } catch {
        // AbortError thrown when destroy() cancels an in-flight load — safe to ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Init only once

  // Load Audio Source
  React.useEffect(() => {
    const ws = wavesurfer.current;
    if (!ws || !url) return;

    let blobUrl: string | null = null;
    let isCurrentSource = true;
    setIsReady(false);

    const loadPromise =
      url instanceof Blob ? ((blobUrl = URL.createObjectURL(url)), ws.load(blobUrl)) : ws.load(url);

    // Wavesurfer rejects when a source is replaced or the component unmounts.
    // Keep that media-load failure inside the player instead of creating an
    // unhandled promise rejection in the browser.
    void loadPromise.catch(() => {
      if (isCurrentSource) setIsReady(false);
    });

    return () => {
      isCurrentSource = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [url]);

  const togglePlayPause = React.useCallback(() => {
    wavesurfer.current?.playPause();
  }, []);

  const seekTo = React.useCallback((progress: number) => {
    wavesurfer.current?.seekTo(progress);
  }, []);

  return {
    containerRef,
    isPlaying,
    currentTime,
    duration,
    isReady,
    togglePlayPause,
    seekTo,
  };
};
